import { codePublicationMode } from "@/features/site-code-update/model";
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/auth";
import { queryClient } from "@/lib/queryClient";
import { demoAxiosAdapter } from "@/lib/demoApi";
import { isDemoMode, isDemoSession } from "@/lib/demoMode";
import {
  isCurrentAuthResponse,
  isCurrentProviderFailure,
  isDispatchableAuthRequest,
  redactAuthorizationFromError,
  type AuthSource,
} from "@/lib/providerAuth";
import {
  activeProviderAccountUuid,
  activeProviderAccessToken,
  discardActiveProviderSession,
  discardProviderSession,
  hasActiveProviderTokens,
  logoutActiveProviderSession,
  refreshActiveProviderSession,
} from "@/lib/providerSession";

type ProviderBoundRequestConfig = InternalAxiosRequestConfig & {
  _providerAccountUuid?: string;
  _authSource?: AuthSource;
};
import type {
  Request,
  RequestDesignAsset,
  RequestPayload,
  ListResponse,
  ClientSite,
  Feedback,
  FeedbackMessage,
  DashboardStats,
  InviteCode,
  PendingUser,
  Manager,
  AdminGroup,
  AdminGroupMember,
  ProfileStats,
  Supervisor,
  Director,
  Client,
  SupportTicket,
  SupportTicketAttachment,
  SupportTicketMessage,
  SupportTicketTemplate,
  GroupCapacity,
  UpdateMaxMembersRequest,
  UpdateMaxMembersResponse,
  SiteDeletionRequest,
  OwnerDashboardStats,
  TimeseriesPoint,
  StatusDistribution,
  TopManager,
  RecentActivity,
  UserGamificationProfile,
  EnhancedProfileResponse,
  AchievementWithProgress,
  LevelConfig,
  LeaderboardEntry,
  GamificationStats,
  Lead,
  LeadDashboard,
  LeadEscalation,
  ManagerActivity,
  WenlixAnalyticsKPI,
  WenlixAnalyticsTimeseriesPoint,
  WenlixAnalyticsCity,
  WenlixAnalyticsFunnel,
  WenlixAnalyticsPageStat,
  WenlixAnalyticsSectionStat,
  WenlixAnalyticsClickStat,
  WenlixAnalyticsSourceStat,
  WenlixAnalyticsDeviceBreakdown,
  WenlixAnalyticsRecentEvent,
  WenlixAnalyticsSessionSummary,
  WenlixAnalyticsSessionDetail,
  WenlixAnalyticsLead,
  SourcePackage,
  SiteRevisionJob,
  SiteRevisionJobAsset,
  SiteEditorState,
  SiteEditorMediaUploadResult,
  AdminManualPricingReviewDecision,
  AdminBillingTrialBackfillResult,
  AdminBillingProviderEvent,
  AdminBillingOperationalSnapshot,
  AdminBillingOfferingsResponse,
  AdminBillingSitesResponse,
  AdminBillingSiteDetail,
  AdminBankTransferSettlementRequest,
  AdminBankTransferSettlementResult,
  AdminRefundPaymentRequest,
  AdminRefundPaymentResult,
  AdminStudioAccessResult,
  BillingBundleActivationResult,
  ManualPricingReview,
  DunningRun,
	  LifecycleOutboxItem,
	  BillingInvoice,
	  BillingProfile,
	  BillingDocument,
	  BankOperation,
	  TBankSyncResult,
  CreateIntakeLinkData,
  IntakeDetail,
  IntakeImportResult,
  IntakeLinksResponse,
  PaymentPortalLink,
  SiteDevelopmentPaymentState,
} from "@/lib/types";
import type {
  OwnerRegistrationRequest,
  OwnerRegistrationResult,
} from "@/lib/ownerRegistration";

// API URLs - wenlix-core uses /api/v1, auth-service uses /api/auth
// Use absolute paths to avoid issues with relative URLs in SPA routing
const API_URL = import.meta.env.VITE_API_URL || "/api/v1";
const AUTH_URL = import.meta.env.VITE_AUTH_URL || "/api";

// Helper to ensure absolute URLs (prevents SPA routing issues)
// When baseURL starts with /, axios should treat it as absolute, but we'll be extra safe
// by ensuring it's always treated as absolute from the root
const ensureAbsoluteURL = (url: string): string => {
  // If it's already an absolute URL (starts with http:// or https://), return as is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  // Ensure it starts with / to make it absolute from root
  return url.startsWith("/") ? url : `/${url}`;
};

// Token management
export const getActiveAccessToken = () => {
  if (isDemoSession()) return null;
  const source = useAuthStore.getState().authSource;
  return source === "provider"
    ? activeProviderAccessToken()
    : localStorage.getItem("access_token");
};
const getRefreshToken = () => localStorage.getItem("refresh_token");
const setTokens = (access: string, refresh: string) => {
  localStorage.setItem("access_token", access);
  localStorage.setItem("refresh_token", refresh);
};
const clearTokens = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
};

// Check if user has valid tokens
export const hasValidTokens = () => {
  const source = useAuthStore.getState().authSource;
  if (source === "provider") return hasActiveProviderTokens();
  if (source !== "legacy") return false;
  return !!(localStorage.getItem("access_token") && getRefreshToken());
};

// Main API client
export const api = axios.create({
  baseURL: ensureAbsoluteURL(API_URL),
  headers: {
    "Content-Type": "application/json",
  },
  ...(isDemoMode ? { adapter: demoAxiosAdapter } : {}),
});

// Auth API client
export const authApi = axios.create({
  baseURL: ensureAbsoluteURL(AUTH_URL),
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for authApi - ensure URLs are properly combined with baseURL
authApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Ensure URLs without leading / are properly combined with baseURL
    // This prevents SPA routing issues when on routes like /register/:code
    if (config.url && config.baseURL && !config.url.startsWith("http://") && !config.url.startsWith("https://")) {
      // If URL doesn't start with /, axios will combine it with baseURL correctly
      // If URL starts with /, it's treated as absolute and baseURL is ignored
      // We want to ensure baseURL is always used, so we remove leading / if present
      if (config.url.startsWith("/") && config.baseURL) {
        config.url = config.url.slice(1);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Request interceptor - add auth token and handle FormData
api.interceptors.request.use(
  (config: ProviderBoundRequestConfig) => {
    const token = getActiveAccessToken();
    const providerAccountUuid = activeProviderAccountUuid();
    const authSource = useAuthStore.getState().authSource;
    if (!isDispatchableAuthRequest(
      config._authSource,
      config._providerAccountUuid ?? null,
      authSource,
      providerAccountUuid,
    )) {
      throw new Error("auth context changed before request dispatch");
    }
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (authSource === "provider" && providerAccountUuid) {
      config._providerAccountUuid = providerAccountUuid;
    }
    config._authSource = authSource;

    // IMPORTANT: For FormData requests, delete Content-Type header
    // so the browser can set it automatically with the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
  accountUuid: string | null;
}> = [];

const processQueue = (
  error: unknown,
  token: string | null = null,
  accountUuid: string | null = null,
) => {
  failedQueue.forEach((prom) => {
    if (error || prom.accountUuid !== accountUuid) {
      prom.reject(error ?? new Error("provider account changed during refresh"));
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Handle auth failure - clear everything and force redirect to login
const handleAuthFailure = (expectedProviderAccountUuid: string | null = null): boolean => {
  const authStore = useAuthStore.getState();
  if (authStore.authSource === "provider") {
    const activeAccountUuid = activeProviderAccountUuid();
    const currentUserAccountUuid = authStore.user?.account_uuid ?? null;
    if (!isCurrentProviderFailure(
      expectedProviderAccountUuid,
      activeAccountUuid,
      currentUserAccountUuid,
    )) {
      if (expectedProviderAccountUuid) discardProviderSession(expectedProviderAccountUuid);
      return false;
    }
    if (expectedProviderAccountUuid) {
      discardProviderSession(expectedProviderAccountUuid);
    } else {
      discardActiveProviderSession();
    }
  }
  clearTokens();
  // Clear zustand store
  authStore.clearUser();
  // Clear React Query cache to prevent stale data from dead session
  queryClient.clear();
  // Force redirect to login as a failsafe
  // ProtectedRoute should also redirect, but this covers edge cases
  // where React render cycle is disrupted (e.g. component errors)
  if (typeof window !== 'undefined' &&
      window.location.pathname !== '/login' &&
      window.location.pathname !== '/register' &&
      !window.location.pathname.startsWith('/register/')) {
    window.location.href = '/login';
  }
  return true;
};

api.interceptors.response.use(
  (response) => {
    const responseConfig = response.config as ProviderBoundRequestConfig;
    if (!isCurrentAuthResponse(
      responseConfig?._authSource ?? null,
      responseConfig?._providerAccountUuid ?? null,
      useAuthStore.getState().authSource,
      activeProviderAccountUuid(),
    )) {
      return Promise.reject(new Error("auth context changed before response completion"));
    }
    return response;
  },
  async (error: AxiosError) => {
    redactAuthorizationFromError(error);
    const originalRequest = error.config as ProviderBoundRequestConfig & {
      _retry?: boolean;
    };
    const boundProviderAccountUuid = originalRequest?._providerAccountUuid ?? null;
    const boundAuthSource = originalRequest?._authSource ?? null;
    const activeAuthSource = useAuthStore.getState().authSource;

    if (!isCurrentAuthResponse(
      boundAuthSource,
      boundProviderAccountUuid,
      activeAuthSource,
      activeProviderAccountUuid(),
    )) {
      return Promise.reject(error);
    }

    // Handle 403 Forbidden - check error codes according to API documentation
    if (error.response?.status === 403) {
      const responseData = error.response?.data as { error?: string; message?: string; block_reason?: string; reason?: string; rejection_reason?: string };
      const errorCode = responseData?.error || '';
      const errorMessage = responseData?.message || '';
      const lowerMessage = errorMessage.toLowerCase();

      // Handle ERR_ACCOUNT_BLOCKED or ERR_ACCOUNT_BANNED - redirect to banned page
      if (errorCode === 'ERR_ACCOUNT_BLOCKED' || errorCode === 'ERR_ACCOUNT_BANNED' ||
          errorCode === 'account_blocked' ||
          lowerMessage.includes('blocked') || lowerMessage.includes('banned')) {
        // Store block reason if available
        if (responseData?.block_reason) {
          sessionStorage.setItem('block_reason', responseData.block_reason);
        } else if (responseData?.reason) {
          sessionStorage.setItem('block_reason', responseData.reason);
        } else if (responseData?.message && !responseData.message.toLowerCase().includes('blocked')) {
          sessionStorage.setItem('block_reason', responseData.message);
        }
        const failedCurrentAccount = handleAuthFailure(boundProviderAccountUuid);
        if (failedCurrentAccount && typeof window !== 'undefined' && window.location.pathname !== '/banned') {
          window.location.href = '/banned';
        }
        return Promise.reject(error);
      }

      // Handle account_rejected or session_invalidated (sent after rejection)
      if (errorCode === 'ERR_ACCOUNT_REJECTED' || errorCode === 'account_rejected' ||
          errorCode === 'session_invalidated' ||
          lowerMessage.includes('rejected') || lowerMessage.includes('отклонен') ||
          lowerMessage.includes('session_invalidated') || lowerMessage.includes('session invalidated')) {
        // Store rejection reason if available
        if (responseData?.message && !responseData.message.toLowerCase().includes('session')) {
          sessionStorage.setItem('rejection_reason', responseData.message);
        }
        const failedCurrentAccount = handleAuthFailure(boundProviderAccountUuid);
        if (failedCurrentAccount && typeof window !== 'undefined' && window.location.pathname !== '/rejected') {
          window.location.href = '/rejected';
        }
        return Promise.reject(error);
      }

      // Handle ERR_ACCOUNT_PENDING - Don't redirect if already on pending page
      if (errorCode === 'ERR_ACCOUNT_PENDING' || errorCode === 'account_pending' ||
          lowerMessage.includes('pending') || lowerMessage.includes('approval')) {
        if (typeof window !== 'undefined' && window.location.pathname !== '/pending') {
          window.location.href = '/pending';
        }
        return Promise.reject(error);
      }

      // For other 403 errors, just reject
      return Promise.reject(error);
    }

    // Don't retry for non-401 errors
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Don't retry if already retrying this request
    if (originalRequest._retry) {
      handleAuthFailure(boundProviderAccountUuid);
      return Promise.reject(error);
    }

    // Handle 401 - try to refresh token
    if (isRefreshing) {
      // Wait for the ongoing refresh to complete
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, accountUuid: boundProviderAccountUuid });
      })
        .then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const authSource = useAuthStore.getState().authSource;
    const refreshAccountUuid = authSource === "provider" ? activeProviderAccountUuid() : null;
    const refreshToken = authSource === "legacy" ? getRefreshToken() : null;
    if ((authSource === "provider" && !refreshAccountUuid) ||
        (authSource !== "provider" && !refreshToken)) {
      isRefreshing = false;
      handleAuthFailure(refreshAccountUuid);
      return Promise.reject(error);
    }
    const refreshContextIsCurrent = () => isCurrentAuthResponse(
      authSource,
      refreshAccountUuid,
      useAuthStore.getState().authSource,
      activeProviderAccountUuid(),
    );

    try {
      let accessToken: string;
      if (authSource === "provider") {
        accessToken = await refreshActiveProviderSession();
        if (!refreshContextIsCurrent()) {
          throw new Error("auth context changed during refresh");
        }
      } else {
        // Use absolute URL to prevent issues with SPA routing
        // When on /admin/users or /requests/new, relative paths can be misinterpreted
        let refreshUrl: string;
        if (AUTH_URL.startsWith('http')) {
          refreshUrl = AUTH_URL.endsWith('/')
            ? `${AUTH_URL}auth/refresh`
            : `${AUTH_URL}/auth/refresh`;
        } else {
          const basePath = AUTH_URL.startsWith('/') ? AUTH_URL : `/${AUTH_URL}`;
          refreshUrl = `${window.location.origin}${basePath}/auth/refresh`;
        }

        const response = await axios.post(refreshUrl, {
          refresh_token: refreshToken,
        }, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (!refreshContextIsCurrent()) {
          throw new Error("auth context changed during refresh");
        }
        accessToken = response.data.access_token;
        setTokens(accessToken, response.data.refresh_token);
      }

      if (!refreshContextIsCurrent()) {
        throw new Error("auth context changed during refresh");
      }

      // Update the request headers with new token
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      }

      // Notify queued requests about the new token
      processQueue(null, accessToken, refreshAccountUuid);

      // Invalidate all React Query caches after token refresh
      // This ensures all components fetch fresh data with the new token
      // preventing stale data desync between components
      queryClient.invalidateQueries();

      // Retry the original request with new token
      return api(originalRequest);
    } catch (refreshError) {
      redactAuthorizationFromError(refreshError);
      processQueue(refreshError, null, refreshAccountUuid);
      if (refreshContextIsCurrent()) {
        handleAuthFailure(refreshAccountUuid);
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ==================== API Helper Functions ====================

export const apiClient = {
  get: <T>(url: string, signal?: AbortSignal) => api.get<T>(url, { signal }).then((res) => res.data),
  post: <T>(url: string, data?: unknown) => api.post<T>(url, data).then((res) => res.data),
  put: <T>(url: string, data?: unknown) => api.put<T>(url, data).then((res) => res.data),
  patch: <T>(url: string, data?: unknown) => api.patch<T>(url, data).then((res) => res.data),
  delete: <T>(url: string, data?: unknown) => api.delete<T>(url, { data }).then((res) => res.data),
};

const filenameFromContentDisposition = (value?: string): string | null => {
  if (!value) {
    return null;
  }
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
  }
  const plainMatch = value.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || null;
};

// ==================== Auth API ====================

// Auth response types according to API documentation
export interface AuthUser {
  id: string;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  phone?: string;
  role: string;
  approval_status?: "pending" | "approved" | "rejected";
  status?: "active" | "pending" | "blocked" | "banned" | "rejected";
  is_blocked?: boolean;
  block_reason?: string | null;
  registration_completed_at?: string;
  created_at?: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
  user: AuthUser;
}

export const authClient = {
  login: async (email: string, password: string) => {
    const response = await authApi.post<AuthResponse>("auth/web/login", { email, password });
    setTokens(response.data.access_token, response.data.refresh_token);
    return response.data;
  },

  register: async (data: {
    email: string;
    password: string;
    invite_code: string; // Required according to API docs
    first_name?: string;
    last_name?: string;
    full_name?: string;
    phone?: string;
  }) => {
    const response = await authApi.post<AuthResponse>("auth/web/register", data);
    setTokens(response.data.access_token, response.data.refresh_token);
    return response.data;
  },

  // Registration with email verification (2-step flow)
  registerStart: async (data: {
    email: string;
    password: string;
    invite_code: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    phone?: string;
  }) => {
    const response = await authApi.post<{
      email: string;
      ttl_sec: number;
      code_sent: boolean;
      code?: string; // Only in dev mode
    }>("auth/web/register/start", data);
    return response.data;
  },

  registerVerify: async (email: string, code: string) => {
    const response = await authApi.post<AuthResponse>("auth/web/register/verify", {
      email,
      code,
    });
    setTokens(response.data.access_token, response.data.refresh_token);
    return response.data;
  },

  logout: async () => {
    const authStore = useAuthStore.getState();
    if (isDemoSession()) {
      authStore.logout();
      queryClient.clear();
      return;
    }
    if (authStore.authSource === "provider") {
      try {
        await logoutActiveProviderSession();
      } finally {
        authStore.clearUser();
        queryClient.clear();
      }
      return;
    }
    clearTokens();
    authStore.logout();
  },

  isAuthenticated: () => !!getActiveAccessToken(),

  // Get current user info
  me: async () => {
    const response = await authApi.get<{ success: boolean; user: AuthUser }>("auth/web/me", {
      headers: { Authorization: `Bearer ${getActiveAccessToken()}` }
    });
    return response.data;
  },

  // Password reset - according to API docs
  passwordResetStart: async (login: string) => {
    const response = await authApi.post<{
      sent_to_email: boolean;
      message: string;
    }>("auth/password/reset/start", { login });
    return response.data;
  },

  passwordResetValidateCode: async (login: string, code: string) => {
    const response = await authApi.post<{ ok: boolean }>("auth/password/reset/validate-code", {
      login,
      code,
    });
    return response.data;
  },
  passwordResetConfirm: async (login: string, code: string, newPassword: string) => {
    const response = await authApi.post<{ ok: boolean }>("auth/password/reset/confirm", {
      login,
      code,
      new_password: newPassword,
    });
    return response.data;
  },
};

// ==================== Requests API ====================

export const requestsApi = {
  normalizeStatus: (status: string) => (status === "canceled" ? "cancelled" : status),

  list: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    tariff?: string;
    premium_approval_status?: string;
    workflow_stage?: string;
  }) => {
    // Filter out undefined/null values and convert to strings
    const queryParams: Record<string, string> = {};
    if (params?.page !== undefined) queryParams.page = String(params.page);
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);
    if (params?.status) queryParams.status = requestsApi.normalizeStatus(params.status);
    if (params?.tariff) queryParams.tariff = params.tariff;
    if (params?.premium_approval_status) queryParams.premium_approval_status = params.premium_approval_status;
    if (params?.workflow_stage) queryParams.workflow_stage = params.workflow_stage;

    const queryString = new URLSearchParams(queryParams).toString();
    const url = queryString ? `/requests?${queryString}` : "/requests";
    return apiClient.get<ListResponse<Request>>(url);
  },

  get: (id: string) => apiClient.get<Request>(`/requests/${id}`),

  exportContent: async (id: string) => {
    try {
      const response = await api.get<Blob>(`/requests/${id}/export`, {
        responseType: "blob",
        timeout: 65000,
      });
      if (!(response.data instanceof Blob) || !String(response.headers["content-type"]).includes("application/zip")) {
        throw new Error("Сервер не вернул архив заявки.");
      }
      return {
        blob: response.data,
        filename: filenameFromContentDisposition(response.headers["content-disposition"]) ||
          `wenlix-request-${id}.zip`,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
        const message = await error.response.data.text();
        if (message && !message.trim().startsWith("<")) throw new Error(message);
      }
      throw new Error("Не удалось скачать архив. Повторите попытку.");
    }
  },

  create: (data: {
    company_name: string;
    client_name?: string;
    client_contact?: string;
    tariff?: string;
    client_has_ready_design?: boolean;
    design_brief_text?: string;
    reference_urls?: string[];
    payload?: RequestPayload;
  }) => apiClient.post<Request>("/requests", data),

  update: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/requests/${id}`, data),

  delete: (id: string) => apiClient.delete(`/requests/${id}`),

  updateStatus: (id: string, status: string) =>
    apiClient.patch(`/requests/${id}/status`, { status: requestsApi.normalizeStatus(status) }),

  archive: (id: string) => apiClient.post(`/requests/${id}/archive`),

  generate: (id: string) => apiClient.post(`/requests/${id}/generate`),

  premium: {
    requestApproval: (id: string) => apiClient.post(`/requests/${id}/premium/request-approval`),
    approve: (id: string) => apiClient.post(`/requests/${id}/premium/approve`),
    reject: (id: string, reason?: string) => apiClient.post(`/requests/${id}/premium/reject`, { reason: reason || "" }),
  },

  cancel: (id: string) => apiClient.post(`/requests/${id}/cancel`),

  pause: (id: string) => apiClient.post(`/requests/${id}/pause`),

  resume: (id: string) => apiClient.post(`/requests/${id}/resume`),

  uploadPhotos: (id: string, formData: FormData) =>
    api.post(`/requests/${id}/photos`, formData).then((res) => res.data),

  designAssets: {
    list: (id: string) => apiClient.get<{ items: RequestDesignAsset[] }>(`/requests/${id}/design-assets`),
    upload: (id: string, formData: FormData) =>
      api.post<{ items: RequestDesignAsset[] }>(`/requests/${id}/design-assets`, formData).then((res) => res.data),
    delete: (id: string, assetId: string) =>
      apiClient.delete(`/requests/${id}/design-assets/${assetId}`),
  },

  deletePhoto: (id: string, photoUrl: string) =>
    api.delete(`/requests/${id}/photos?url=${encodeURIComponent(photoUrl)}`).then((res) => res.data),

  search: (query: string, params?: { page?: number; limit?: number }) => {
    const queryParams: Record<string, string> = { q: query };
    if (params?.page !== undefined) queryParams.page = String(params.page);
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);
    const queryString = new URLSearchParams(queryParams).toString();
    return apiClient.get<ListResponse<Request>>(`/requests/search?${queryString}`);
  },
};

// ==================== Request Intake API ====================

export const intakeLinksApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    core_request_id?: string;
  }) => {
    const queryParams: Record<string, string> = {};
    if (params?.page !== undefined) queryParams.page = String(params.page);
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);
    if (params?.status) queryParams.status = params.status;
    if (params?.core_request_id) queryParams.core_request_id = params.core_request_id;

    const queryString = new URLSearchParams(queryParams).toString();
    const url = queryString ? `/intake-links?${queryString}` : "/intake-links";
    return apiClient.get<IntakeLinksResponse>(url);
  },

  create: (data: CreateIntakeLinkData) =>
    apiClient.post<IntakeDetail["link"]>("/intake-links", data),

  get: (id: string) => apiClient.get<IntakeDetail>(`/intake-links/${id}`),

  revoke: (id: string) => apiClient.post<{ ok: boolean }>(`/intake-links/${id}/revoke`),

  reopen: (id: string) => apiClient.post<{ ok: boolean }>(`/intake-links/${id}/reopen`),

  import: (id: string, coreRequestId?: string) =>
    apiClient.post<IntakeImportResult>(`/intake-links/${id}/import`, {
      mode: coreRequestId ? "update" : "create",
      core_request_id: coreRequestId,
    }),

  importAndGenerate: (id: string, coreRequestId?: string) =>
    apiClient.post<IntakeImportResult>(`/intake-links/${id}/import-and-generate`, {
      mode: coreRequestId ? "update" : "create",
      core_request_id: coreRequestId,
    }),
};

// ==================== Payment Portal API ====================

export const paymentLinksApi = {
  create: (data: { request_id: string; expires_at?: string }) =>
    apiClient.post<PaymentPortalLink>("/payment-links", data),

  paymentState: (requestId: string) =>
    apiClient.get<SiteDevelopmentPaymentState>(`/payment-links/requests/${requestId}/payment-state`),

  revoke: (id: string) => apiClient.post<{ ok: boolean }>(`/payment-links/${id}/revoke`),
};

// ==================== Sites API ====================

export const sitesApi = {
  list: (params?: {
    deploy_status?: string;
    hosting_plan?: string;
    premium_status?: string;
    manual_finish_required?: boolean;
    manual_finish_done?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const queryParams: Record<string, string> = {};
    if (params?.deploy_status) queryParams.deploy_status = params.deploy_status;
    if (params?.hosting_plan) queryParams.hosting_plan = params.hosting_plan;
    if (params?.premium_status) queryParams.premium_status = params.premium_status;
    if (params?.manual_finish_required !== undefined) queryParams.manual_finish_required = String(params.manual_finish_required);
    if (params?.manual_finish_done !== undefined) queryParams.manual_finish_done = String(params.manual_finish_done);
    if (params?.page !== undefined) queryParams.page = String(params.page);
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);

    const queryString = new URLSearchParams(queryParams).toString();
    const url = queryString ? `/sites?${queryString}` : "/sites";
    return apiClient.get<ListResponse<ClientSite>>(url);
  },

  get: (id: string) => apiClient.get<ClientSite>(`/sites/${id}`),

  getByRequest: (requestId: string) => apiClient.get<ClientSite>(`/sites/by-request/${requestId}`),

  create: (data: {
    request_id?: string;
    company_name: string;
    client_name?: string;
    client_contact?: string;
    hosting_plan?: string;
    notes?: string;
  }) => apiClient.post("/sites", data),

  update: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/sites/${id}`, data),

  deploy: (id: string) => apiClient.post(`/sites/${id}/deploy`),

  stop: (id: string) => apiClient.post(`/sites/${id}/stop`),

  assignDomain: (id: string, data: {
    domain: string;
    enable_ssl?: boolean;
    site_type?: string;
    tariff?: string;
  }) => apiClient.post(`/sites/${id}/domain`, data),

  extendHosting: (id: string, plan: string, months: number) =>
    apiClient.post(`/sites/${id}/extend`, { plan, months }),

  delete: (id: string) => apiClient.delete(`/sites/${id}`),

  syncStatus: (id: string) => apiClient.post(`/sites/${id}/sync-status`),

  getHistory: (id: string, limit?: number) =>
    apiClient.get(`/sites/${id}/history?limit=${limit || 10}`),

  downloadEvidence: async (id: string) => {
    const response = await api.get<Blob>(`/sites/${id}/evidence`, {
      responseType: "blob",
    });
    const filename = filenameFromContentDisposition(response.headers["content-disposition"]) ||
      `wenlix-site-evidence-${id}.json`;
    return { blob: response.data, filename };
  },

  premium: {
    start: (id: string, notes?: string) => apiClient.post<ClientSite>(`/sites/${id}/premium/start`, { notes }),
    assign: (id: string, assigneeId: string, notes?: string) =>
      apiClient.post<ClientSite>(`/sites/${id}/premium/assign`, { assignee_id: assigneeId, notes }),
    approveReview: (id: string, notes?: string) =>
      apiClient.post<ClientSite>(`/sites/${id}/premium/approve-review`, { notes }),
  },

  sourcePackages: {
    list: (id: string) => apiClient.get<{ items: SourcePackage[] }>(`/sites/${id}/source-packages`),
    getBaseDownload: (id: string) =>
      apiClient.get<{ download_url: string; package: SourcePackage }>(`/sites/${id}/source-packages/base/download`),
    upload: (id: string, formData: FormData) =>
      api.post<SourcePackage>(`/sites/${id}/source-packages/upload`, formData).then((res) => res.data),
  },

  editor: {
    getState: (siteId: string) =>
      apiClient.get<SiteEditorState>(`/sites/${siteId}/editor/state`),
    saveDraft: (siteId: string, draftData: Record<string, unknown>) =>
      apiClient.put<SiteEditorState>(`/sites/${siteId}/editor/draft`, { draft_data: draftData }),
    publish: (siteId: string) =>
      apiClient.post<SiteEditorState>(`/sites/${siteId}/editor/publish`),
    discard: (siteId: string) =>
      apiClient.post<{ success: boolean }>(`/sites/${siteId}/editor/discard`),
    uploadMedia: (siteId: string, formData: FormData) =>
      api.post<SiteEditorMediaUploadResult>(`/sites/${siteId}/editor/media`, formData).then((res) => res.data),
  },

  revisions: {
    listJobs: (siteId: string) =>
      apiClient.get<{ items: SiteRevisionJob[] }>(`/sites/${siteId}/revisions/jobs`),
    getJob: (siteId: string, jobId: string) =>
      apiClient.get<SiteRevisionJob>(`/sites/${siteId}/revisions/jobs/${jobId}`),
    createJob: (
      siteId: string,
      data: {
        title: string;
        priority?: string;
        change_brief: Record<string, unknown>;
      }
    ) => apiClient.post<SiteRevisionJob>(`/sites/${siteId}/revisions/jobs`, data),
    updateJob: (
      siteId: string,
      jobId: string,
      data: {
        title: string;
        priority?: string;
        change_brief: Record<string, unknown>;
      }
    ) => apiClient.put<SiteRevisionJob>(`/sites/${siteId}/revisions/jobs/${jobId}`, data),
    uploadAsset: (siteId: string, jobId: string, formData: FormData) =>
      api.post<SiteRevisionJobAsset>(`/sites/${siteId}/revisions/jobs/${jobId}/assets`, formData).then((res) => res.data),
    deleteAsset: (siteId: string, jobId: string, assetId: string) =>
      apiClient.delete<void>(`/sites/${siteId}/revisions/jobs/${jobId}/assets/${assetId}`),
    submit: (siteId: string, jobId: string) =>
      apiClient.post<SiteRevisionJob>(`/sites/${siteId}/revisions/jobs/${jobId}/submit`),
    cancel: (siteId: string, jobId: string) =>
      apiClient.post<SiteRevisionJob>(`/sites/${siteId}/revisions/jobs/${jobId}/cancel`),
    approve: (siteId: string, jobId: string) =>
      apiClient.post<SiteRevisionJob>(`/sites/${siteId}/revisions/jobs/${jobId}/approve`),
    reject: (siteId: string, jobId: string) =>
      apiClient.post<SiteRevisionJob>(`/sites/${siteId}/revisions/jobs/${jobId}/reject`),
  },

  // Stats & Plans
  stats: () => apiClient.get("/sites/stats"),
  plans: () => apiClient.get("/sites/plans"),
  expiring: () => apiClient.get<ClientSite[] | { items: ClientSite[] }>("/sites/expiring"),
};

// ==================== Feedback API ====================

export const feedbackApi = {
  list: (params?: { status?: string; page?: number; limit?: number }) => {
    const queryParams: Record<string, string> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.page !== undefined) queryParams.page = String(params.page);
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);

    const queryString = new URLSearchParams(queryParams).toString();
    const url = queryString ? `/feedback?${queryString}` : "/feedback";
    return apiClient.get<ListResponse<Feedback>>(url);
  },

  get: (id: string) => apiClient.get<Feedback>(`/feedback/${id}`),

  create: (data: {
    subject: string;
    message: string;
    category?: string;
    priority?: string;
    request_id?: string;
  }) => apiClient.post("/feedback", data),

  updateStatus: (id: string, status: string) =>
    apiClient.patch(`/feedback/${id}/status`, { status }),

  // Admin methods
  adminList: (params?: { status?: string; page?: number; limit?: number; group_id?: string }) => {
    const queryParams: Record<string, string> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.page !== undefined) queryParams.page = String(params.page);
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);
    if (params?.group_id) queryParams.group_id = params.group_id;
    const queryString = new URLSearchParams(queryParams).toString();
    return apiClient.get(queryString ? `/admin/feedback?${queryString}` : "/admin/feedback");
  },

  adminRespond: (id: string, data: { response: string; status?: string }) =>
    apiClient.post(`/admin/feedback/${id}/respond`, data),

  adminUpdateStatus: (id: string, status: string) =>
    apiClient.patch(`/admin/feedback/${id}/status`, { status }),

  adminGet: (id: string) => apiClient.get<Feedback>(`/admin/feedback/${id}`),

  // Messages endpoints
  getMessages: (id: string) => apiClient.get<FeedbackMessage[]>(`/feedback/${id}/messages`),

  sendMessage: (id: string, data: { message: string }) =>
    apiClient.post<FeedbackMessage>(`/feedback/${id}/messages`, data),

  sendInternalNote: (id: string, data: { message: string }) =>
    apiClient.post<FeedbackMessage>(`/feedback/${id}/internal-note`, data),
};

// ==================== Profile API ====================

export const profileApi = {
  get: () => apiClient.get("/users/me"),

  update: (data: { first_name?: string; last_name?: string; phone?: string; contact?: string }) =>
    apiClient.patch("/users/me", data),

  stats: () => apiClient.get<ProfileStats>("/users/me/stats"),

  // Get user permissions based on role and group
  permissions: () => apiClient.get<{ permissions: string[] }>("/users/me/permissions"),

  // Password change goes through auth-service
  changePassword: (data: { current_password: string; new_password: string }) =>
    authApi.post("auth/password/change", data, {
      headers: { Authorization: `Bearer ${getActiveAccessToken()}` }
    }),

  // Update profile (через auth-service)
  updateProfile: (data: { first_name?: string; last_name?: string; full_name?: string; phone?: string }) =>
    authApi.patch("auth/profile", data, {
      headers: { Authorization: `Bearer ${getActiveAccessToken()}` }
    }),
};

// ==================== Admin API ====================

export const adminApi = {
  // Dashboard & Stats
  dashboard: () => apiClient.get<DashboardStats>("/admin/dashboard"),
  auditLog: (params?: { page?: number; limit?: number }) =>
    apiClient.get(`/admin/audit-log?${new URLSearchParams(params as Record<string, string>).toString()}`),
  stats: {
    overview: () => apiClient.get("/admin/stats/overview"),
    byStatus: () => apiClient.get("/admin/stats/by-status"),
    byDay: (days?: number) => apiClient.get(`/admin/stats/by-day?days=${days || 7}`),
    managers: () => apiClient.get("/admin/stats/managers"),
  },

  // All users
  allUsers: {
    list: () => apiClient.get<{ items: Manager[] }>("/admin/users"),
  },

  // Managers
  managers: {
    list: () => apiClient.get<{ items: Manager[] }>("/admin/managers"),
    get: (id: string) => apiClient.get<Manager>(`/admin/managers/${id}`),
    block: (id: string, reason?: string) => apiClient.post(`/admin/managers/${id}/block`, { reason: reason || "" }),
    unblock: (id: string) => apiClient.post(`/admin/managers/${id}/unblock`),
    delete: (id: string) => apiClient.delete(`/admin/managers/${id}`),
    makeSupervisor: (id: string) => apiClient.post(`/admin/managers/${id}/make-supervisor`),
    moveToGroup: (id: string, groupId: string) =>
      apiClient.post(`/admin/managers/${id}/move-group`, { group_id: groupId }),
  },

  // Role assignment (owner only)
  assignRole: (userId: string, role: string) =>
    apiClient.patch(`/admin/users/${userId}/role`, { role }),

  // Supervisors & Directors
  supervisors: {
    list: () => apiClient.get<{ items: Supervisor[] }>("/admin/supervisors"),
    get: (id: string) => apiClient.get<Supervisor>(`/admin/managers/${id}`),
    block: (id: string) => apiClient.post(`/admin/managers/${id}/block`),
    unblock: (id: string) => apiClient.post(`/admin/managers/${id}/unblock`),
    delete: (id: string) => apiClient.delete(`/admin/managers/${id}`),
    assignRole: (id: string, role: string) => apiClient.post(`/admin/users/${id}/assign-role`, { role }),
  },
  directors: {
    list: () => apiClient.get<{ items: Director[] }>("/admin/directors"),
    get: (id: string) => apiClient.get<Director>(`/admin/managers/${id}`),
    block: (id: string) => apiClient.post(`/admin/managers/${id}/block`),
    unblock: (id: string) => apiClient.post(`/admin/managers/${id}/unblock`),
    delete: (id: string) => apiClient.delete(`/admin/managers/${id}`),
    assignRole: (id: string, role: string) => apiClient.post(`/admin/users/${id}/assign-role`, { role }),
  },

  // Pending registrations
  pending: {
    list: () => apiClient.get<{ items: PendingUser[] }>("/admin/pending"),
    approve: (id: string) => apiClient.post(`/admin/pending/${id}/approve`),
    reject: (id: string, reason: string) =>
      apiClient.post(`/admin/pending/${id}/reject`, { reason }),
    reset: (id: string) => apiClient.post(`/admin/pending/${id}/reset`),
  },

  // Users API (general user management)
  users: {
    listManagers: () => apiClient.get<{ items: Manager[] }>("/users/managers"),
    listAdmins: () => apiClient.get<{ items: Manager[] }>("/users/admins"),
    listPending: () => apiClient.get<{ items: PendingUser[] }>("/users/pending"),
    get: (id: string) => apiClient.get(`/users/${id}`),
    approve: (id: string) => apiClient.post(`/users/${id}/approve`),
    reject: (id: string) => apiClient.post(`/users/${id}/reject`),
    block: (id: string) => apiClient.post(`/users/${id}/block`),
    unblock: (id: string) => apiClient.post(`/users/${id}/unblock`),
    promote: (id: string) => apiClient.post(`/users/${id}/promote`),
    delete: (id: string) => apiClient.delete(`/users/${id}`),
  },

  // Groups
  groups: {
    list: () => apiClient.get<{ items: AdminGroup[] }>("/admin/groups"),
    create: (data: { name: string; description?: string; permissions: string[] }) =>
      apiClient.post<AdminGroup>("/admin/groups", data),
    get: (id: string) => apiClient.get<AdminGroup>(`/admin/groups/${id}`),
    update: (id: string, data: { name?: string; description?: string; permissions?: string[] }) =>
      apiClient.put(`/admin/groups/${id}`, data),
    delete: (id: string) => apiClient.delete(`/admin/groups/${id}`),
    getMembers: (groupId: string) =>
      apiClient.get<AdminGroupMember[] | { items: AdminGroupMember[] }>(`/admin/groups/${groupId}/members`),
    addMember: (groupId: string, userId: string) =>
      apiClient.post(`/admin/groups/${groupId}/members`, { user_id: userId }),
    removeMember: (groupId: string, userId: string) =>
      apiClient.delete(`/admin/groups/${groupId}/members/${userId}`),
    assignSupervisor: (groupId: string, supervisorId: string | null) =>
      apiClient.post(`/admin/groups/${groupId}/supervisor`, { supervisor_id: supervisorId || null }),
    // Capacity endpoints
    getCapacity: () => apiClient.get<{ groups: GroupCapacity[] }>("/admin/groups/capacity"),
    getGroupCapacity: (groupId: string) => apiClient.get<GroupCapacity>(`/admin/groups/${groupId}/capacity`),
    updateMaxMembers: (groupId: string, maxMembers: number) =>
      apiClient.patch<UpdateMaxMembersResponse>(`/admin/groups/${groupId}/max-members`, { max_members: maxMembers } as UpdateMaxMembersRequest),
  },

  // Invite codes (через wenlix-core API)
  invites: {
    list: () =>
      apiClient.get<{ items: InviteCode[] }>("/admin/invites"),
    create: (data: {
      target_role?: string;
      max_uses: number;
      expires_in_days?: number;
      group_id?: string;
      auto_approve?: boolean;
    }) =>
      apiClient.post<InviteCode>("/admin/invites", {
        target_role: data.target_role || "manager",
        max_uses: data.max_uses,
        expires_in_days: data.expires_in_days,
        group_id: data.group_id,
        auto_approve: data.auto_approve || false,
      }),
    delete: (id: string) =>
      apiClient.delete(`/admin/invites/${id}`),
    deactivate: (id: string) =>
      apiClient.post(`/admin/invites/${id}/deactivate`, {}),
    validate: (code: string) => {
      // Public endpoint for invite validation - uses auth service
      // According to API docs: GET /auth/invite/validate/{code}
      const baseUrl = AUTH_URL.startsWith("http") ? AUTH_URL : `${window.location.origin}${ensureAbsoluteURL(AUTH_URL)}`;
      const url = `${baseUrl}/auth/invite/validate/${encodeURIComponent(code)}`;
      return axios.get<{
        valid: boolean;
        name?: string;
        role_to_assign?: string;
        role?: string; // Alias for role_to_assign
        auto_approve?: boolean;
        message?: string;
        error?: string;
        created_by?: string;
      }>(url).then(res => res.data);
    },
    // Also support wenlix-core validation endpoint
    validateCore: (code: string) => {
      const baseUrl = API_URL.startsWith("http") ? API_URL : `${window.location.origin}${ensureAbsoluteURL(API_URL)}`;
      const url = `${baseUrl}/admin/invites/validate/${encodeURIComponent(code)}`;
      return axios.get<{
        valid: boolean;
        role?: string;
        created_by?: string;
        error?: string
      }>(url).then(res => res.data);
    },
  },

  // Broadcast
  broadcast: (data: {
    message: string;
    target: string;
    user_ids?: string[];
    send_email?: boolean;
  }) =>
    apiClient.post<{
      success: boolean;
      recipients_count: number;
      emails_sent: number;
      target: string;
      message: string;
    }>("/admin/broadcast", data),

  // Export
  export: {
    users: () => api.get("/admin/export/users", { responseType: "blob" }),
    sites: () => api.get("/admin/export/sites", { responseType: "blob" }),
    requests: () => api.get("/admin/export/requests", { responseType: "blob" }),
  },

  // Requests management (admin view - uses regular requests endpoint which returns all for admins)
  requests: {
    list: (params?: { page?: number; limit?: number; status?: string }) =>
      apiClient.get<ListResponse<Request>>(`/requests?${new URLSearchParams(params as Record<string, string>).toString()}`),
    massArchive: (ids: string[]) =>
      apiClient.post("/admin/requests/mass-archive", { ids }),
    massDelete: (ids: string[], confirmationCode?: string) =>
      apiClient.post("/admin/requests/mass-delete", { ids, confirmation_code: confirmationCode }),
  },

  // Sites management (admin view)
  sites: {
    list: (params?: { page?: number; limit?: number; deploy_status?: string }) => {
      const queryParams: Record<string, string> = {};
      if (params?.page !== undefined) queryParams.page = String(params.page);
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      if (params?.deploy_status) queryParams.deploy_status = params.deploy_status;
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<ListResponse<ClientSite>>(queryString ? `/sites?${queryString}` : "/sites");
    },
    get: (id: string) => apiClient.get<ClientSite>(`/sites/${id}`),
    update: (id: string, data: Partial<ClientSite>) => apiClient.put<ClientSite>(`/sites/${id}`, data),
    delete: (id: string) => apiClient.delete(`/sites/${id}`),
    deploy: (id: string) => apiClient.post(`/sites/${id}/deploy`),
    stop: (id: string) => apiClient.post(`/sites/${id}/stop`),
  },

  // Site deletion requests (manager requests, supervisor+ approves)
  siteDeletionRequests: {
    list: (params?: { page?: number; limit?: number; status?: string }) => {
      const queryParams: Record<string, string> = {};
      if (params?.page !== undefined) queryParams.page = String(params.page);
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      if (params?.status) queryParams.status = params.status;
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<{ items: SiteDeletionRequest[]; page: number; limit: number }>(
        queryString ? `/site-deletion-requests?${queryString}` : "/site-deletion-requests"
      );
    },
    listMy: (params?: { page?: number; limit?: number }) => {
      const queryParams: Record<string, string> = {};
      if (params?.page !== undefined) queryParams.page = String(params.page);
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<{ items: SiteDeletionRequest[]; page: number; limit: number }>(
        queryString ? `/site-deletion-requests/my?${queryString}` : "/site-deletion-requests/my"
      );
    },
    get: (id: string) => apiClient.get<SiteDeletionRequest>(`/site-deletion-requests/${id}`),
    create: (data: { site_id: string; reason: string }) =>
      apiClient.post<SiteDeletionRequest>("/site-deletion-requests", data),
    approve: (id: string, comment?: string) =>
      apiClient.post<SiteDeletionRequest>(`/site-deletion-requests/${id}/approve`, { comment }),
    reject: (id: string, comment?: string) =>
      apiClient.post<SiteDeletionRequest>(`/site-deletion-requests/${id}/reject`, { comment }),
    countPending: () => apiClient.get<{ count: number }>("/site-deletion-requests/count/pending"),
  },

  billing: {
    offerings: () => apiClient.get<AdminBillingOfferingsResponse>("/admin/billing/offerings"),
    listSites: (params?: { status?: string; search?: string; limit?: number }) => {
      const queryParams: Record<string, string> = {};
      if (params?.status) queryParams.status = params.status;
      if (params?.search) queryParams.search = params.search;
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<AdminBillingSitesResponse>(queryString ? `/admin/billing/sites?${queryString}` : "/admin/billing/sites");
    },
    getSite: (siteId: string) => apiClient.get<AdminBillingSiteDetail>(`/admin/billing/sites/${siteId}`),
    bootstrapTrial: (siteId: string, data?: {
      owner_auth_user_id?: string;
      plan_code?: string;
      days?: number;
      reason?: string;
    }) => apiClient.post<AdminStudioAccessResult>(`/admin/billing/sites/${siteId}/bootstrap-trial`, data || {}),
    grantComplimentaryDays: (siteId: string, data: {
      owner_auth_user_id?: string;
      plan_code?: string;
      days: number;
      reason?: string;
      comment?: string;
    }) => apiClient.post<AdminStudioAccessResult>(`/admin/billing/sites/${siteId}/grant-complimentary-days`, data),
    acceptSite: (siteId: string, data?: {
      accepted_at?: string;
      reason?: string;
      comment?: string;
    }) => apiClient.post<BillingBundleActivationResult>(`/admin/billing/sites/${siteId}/acceptance`, data || {}),
    recalculateSeats: (siteId: string) => apiClient.post(`/admin/billing/sites/${siteId}/recalculate-seats`, {}),
    suspend: (siteId: string, data?: { restore_until?: string; reason?: string; comment?: string }) =>
      apiClient.post(`/admin/billing/sites/${siteId}/suspend`, data || {}),
    restore: (siteId: string, data?: { reason?: string; comment?: string }) =>
      apiClient.post(`/admin/billing/sites/${siteId}/restore`, data || {}),
    markUnpublished: (siteId: string, data?: { at?: string; reason?: string; comment?: string }) =>
      apiClient.post(`/admin/billing/sites/${siteId}/mark-unpublished`, data || {}),
    markPurged: (siteId: string, data?: { at?: string; reason?: string; comment?: string }) =>
      apiClient.post(`/admin/billing/sites/${siteId}/mark-purged`, data || {}),
    purge: (siteId: string, data?: { reason?: string; comment?: string }) =>
      apiClient.post(`/admin/billing/sites/${siteId}/purge`, data || {}),
    createManualInvoice: (siteId: string, data: {
      amount_minor?: number;
      currency?: string;
      description?: string;
      plan_code?: string;
      development_plan_code?: string;
      studio_addon_code?: string;
      subject_type?: string;
      payment_channel?: "card" | "sbp" | "bank_transfer" | "payment_link";
      owner_auth_user_id?: string;
      billing_profile_id?: string;
      billing_profile?: BillingProfile;
      period_start?: string;
      period_end?: string;
      due_at?: string;
    }) =>
      apiClient.post<BillingInvoice>(`/admin/billing/sites/${siteId}/manual-invoices`, data),
    listDocuments: (params: { site_id: string; limit?: number }) => {
      const queryParams: Record<string, string> = { site_id: params.site_id };
      if (params.limit !== undefined) queryParams.limit = String(params.limit);
      return apiClient.get<{ items: BillingDocument[]; total: number }>(
        `/admin/billing/documents?${new URLSearchParams(queryParams).toString()}`
      );
    },
    downloadDocument: async (documentId: string, params: { site_id: string; file_name?: string }) => {
      const queryParams = new URLSearchParams({ site_id: params.site_id });
      const response = await api.get<Blob>(
        `/admin/billing/documents/${encodeURIComponent(documentId)}/download?${queryParams.toString()}`,
        { responseType: "blob" }
      );
      return {
        blob: response.data,
        fileName: filenameFromContentDisposition(response.headers["content-disposition"]) || params.file_name || `${documentId}.pdf`,
      };
    },
    refundPayment: (siteId: string, invoiceId: string, data?: AdminRefundPaymentRequest) =>
      apiClient.post<AdminRefundPaymentResult>(
        `/admin/billing/sites/${siteId}/invoices/${invoiceId}/refunds`,
        data || {}
      ),
    confirmBankTransfer: (siteId: string, invoiceId: string, data?: AdminBankTransferSettlementRequest) =>
      apiClient.post<AdminBankTransferSettlementResult>(
        `/admin/billing/sites/${siteId}/invoices/${invoiceId}/bank-transfer/confirm`,
        data || {}
      ),
	    rejectBankTransfer: (siteId: string, invoiceId: string, data?: AdminBankTransferSettlementRequest) =>
	      apiClient.post<AdminBankTransferSettlementResult>(
	        `/admin/billing/sites/${siteId}/invoices/${invoiceId}/bank-transfer/reject`,
	        data || {}
	      ),
	    listBankOperations: (params?: { status?: string; limit?: number }) => {
	      const queryParams: Record<string, string> = {};
	      if (params?.status) queryParams.status = params.status;
	      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
	      const queryString = new URLSearchParams(queryParams).toString();
	      return apiClient.get<{ items: BankOperation[]; total: number }>(
	        queryString ? `/admin/billing/bank-operations?${queryString}` : "/admin/billing/bank-operations"
	      );
	    },
	    bindBankOperation: (operationId: string, data: { invoice_id: string; reason?: string; comment?: string }) =>
	      apiClient.post<BankOperation>(`/admin/billing/bank-operations/${encodeURIComponent(operationId)}/bind`, data),
	    syncTBank: (data?: { from?: string; to?: string }) =>
	      apiClient.post<TBankSyncResult>("/admin/billing/tbank/sync", data || {}),
	    listManualReviews: (params?: { site_id?: string; status?: string; limit?: number }) => {
      const queryParams: Record<string, string> = {};
      if (params?.site_id) queryParams.site_id = params.site_id;
      if (params?.status) queryParams.status = params.status;
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<{ items: ManualPricingReview[]; total: number }>(
        queryString ? `/admin/billing/manual-reviews?${queryString}` : "/admin/billing/manual-reviews"
      );
    },
    approveManualReview: (
      reviewId: string,
      data: {
        approved_extra_seat_amount_minor: number;
        currency?: string;
        description?: string;
        comment?: string;
        attempt_charge?: boolean;
      }
    ) => apiClient.post<AdminManualPricingReviewDecision>(`/admin/billing/manual-reviews/${reviewId}/approve`, data),
    rejectManualReview: (reviewId: string, data?: { comment?: string }) =>
      apiClient.post<ManualPricingReview>(`/admin/billing/manual-reviews/${reviewId}/reject`, data || {}),
    listDunningRuns: (params?: { site_id?: string; status?: string; limit?: number }) => {
      const queryParams: Record<string, string> = {};
      if (params?.site_id) queryParams.site_id = params.site_id;
      if (params?.status) queryParams.status = params.status;
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<{ items: DunningRun[]; total: number }>(
        queryString ? `/admin/billing/dunning-runs?${queryString}` : "/admin/billing/dunning-runs"
      );
    },
    retryDunningRun: (runId: string) => apiClient.post<DunningRun>(`/admin/billing/dunning-runs/${runId}/retry`, {}),
    listLifecycleOutbox: (params?: { site_id?: string; status?: string; event_type?: string; limit?: number }) => {
      const queryParams: Record<string, string> = {};
      if (params?.site_id) queryParams.site_id = params.site_id;
      if (params?.status) queryParams.status = params.status;
      if (params?.event_type) queryParams.event_type = params.event_type;
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<{ items: LifecycleOutboxItem[]; total: number }>(
        queryString ? `/admin/billing/lifecycle-outbox?${queryString}` : "/admin/billing/lifecycle-outbox"
      );
    },
    retryLifecycleOutbox: (outboxId: string) =>
      apiClient.post<LifecycleOutboxItem>(`/admin/billing/lifecycle-outbox/${outboxId}/retry`, {}),
    retryProviderEvent: (eventId: string) =>
      apiClient.post<AdminBillingProviderEvent>(`/admin/billing/provider-events/${eventId}/retry`, {}),
    getOperationalSnapshot: (params?: { limit?: number }) => {
      const queryParams: Record<string, string> = {};
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<AdminBillingOperationalSnapshot>(
        queryString ? `/admin/billing/operations?${queryString}` : "/admin/billing/operations"
      );
    },
    backfillTrials: (data?: { limit?: number; reason?: string }) =>
      apiClient.post<AdminBillingTrialBackfillResult>("/admin/billing/backfill/trials", data || {}),
  },

  // Clients management
  clients: {
    list: (params?: { page?: number; limit?: number; search?: string }) => {
      const queryParams: Record<string, string> = {};
      if (params?.page !== undefined) queryParams.page = String(params.page);
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      if (params?.search) queryParams.search = params.search;
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<ListResponse<Client>>(queryString ? `/clients?${queryString}` : "/clients");
    },
    get: (id: string) => apiClient.get<Client>(`/clients/${id}`),
    getBySite: (siteId: string) => apiClient.get<Client>(`/clients/site/${siteId}`),
  },

  // Feedback/Appeals management
  feedback: {
    list: (params?: { page?: number; limit?: number; status?: string }) => {
      const queryParams: Record<string, string> = {};
      if (params?.page !== undefined) queryParams.page = String(params.page);
      if (params?.limit !== undefined) queryParams.limit = String(params.limit);
      if (params?.status) queryParams.status = params.status;
      const queryString = new URLSearchParams(queryParams).toString();
      return apiClient.get<ListResponse<Feedback>>(queryString ? `/admin/feedback?${queryString}` : "/admin/feedback");
    },
    get: (id: string) => apiClient.get<Feedback>(`/admin/feedback/${id}`),
    respond: (id: string, data: { response: string; status?: string }) =>
      apiClient.post(`/admin/feedback/${id}/respond`, data),
    updateStatus: (id: string, status: string) =>
      apiClient.patch(`/admin/feedback/${id}/status`, { status }),
    delete: (id: string) => apiClient.delete(`/admin/feedback/${id}`),
  },
};

// ==================== Domain API ====================

export type DomainCheckResult = {
  status: "available" | "taken" | "invalid_domain";
  domain: string;
  price?: {
    currency: string;
    amount: number;
  };
  is_premium?: boolean;
  alternatives?: Array<{
    domain: string;
    price?: {
      currency: string;
      amount: number;
    };
  }>;
  message?: string;
};

export type DomainRegisterResult = {
  status: string;
  domain: string;
  service_id?: string;
  bill_id?: string;
  dns?: Record<string, string>;
  message?: string;
};

export const domainApi = {
  check: (siteId: string, domain: string, tlds?: string[], maxSuggestions?: number) =>
    apiClient.post<DomainCheckResult>(`/domains/${siteId}/check`, {
      domain,
      tlds,
      max_suggestions: maxSuggestions || 5
    }),

  register: (siteId: string, domain: string, period?: number, configureDns?: boolean) =>
    apiClient.post<DomainRegisterResult>(`/domains/${siteId}/register`, {
      domain,
      period: period || 1,
      configure_dns: configureDns !== false
    }),

  getStatus: (siteId: string) =>
    apiClient.get(`/domains/${siteId}/status`),

  getPrices: () => apiClient.get("/domains/prices"),
};

// ==================== Reviews API ====================

export const reviewsApi = {
  scrape: (data: {
    query?: string;
    yandex_maps_url?: string;
    organization_name?: string;
    city?: string;
    max_reviews?: number;
  }) => apiClient.post("/reviews/scrape", data),

  getByUrl: (url: string, maxReviews?: number) =>
    apiClient.get(`/reviews/by-url?url=${encodeURIComponent(url)}&max_reviews=${maxReviews || 10}`),

  search: (name: string, city?: string) =>
    apiClient.get(`/reviews/search?name=${encodeURIComponent(name)}${city ? `&city=${encodeURIComponent(city)}` : ""}`),
};

// ==================== Services API (Additional services) ====================

export const servicesApi = {
  list: () => apiClient.get("/additional-services"),

  getForRequest: (requestId: string) =>
    apiClient.get(`/requests/${requestId}/services`),

  add: (requestId: string, data: { service_id: string; notes?: string; price?: string }) =>
    apiClient.post(`/requests/${requestId}/services`, data),

  update: (
    requestId: string,
    serviceId: string,
    data: { status?: string; notes?: string; price?: string }
  ) => apiClient.patch(`/requests/${requestId}/services/${serviceId}`, data),

  remove: (requestId: string, serviceId: string) =>
    apiClient.delete(`/requests/${requestId}/services/${serviceId}`),
};

// ==================== Clients API ====================

export const clientsApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const queryParams: Record<string, string> = {};
    if (params?.page !== undefined) queryParams.page = String(params.page);
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);
    if (params?.search) queryParams.search = params.search;

    const queryString = new URLSearchParams(queryParams).toString();
    const url = queryString ? `/clients?${queryString}` : "/clients";
    return apiClient.get<ListResponse<Client>>(url);
  },

  register: (data: OwnerRegistrationRequest) =>
    apiClient.post<OwnerRegistrationResult>("/clients/register", data),

  attachExisting: (data: {
    site_id: string;
    existing_auth_user_id: string;
  }) => apiClient.post("/clients/attach-existing", data),

  getBySite: (siteId: string) => apiClient.get<Client>(`/clients/site/${siteId}`),

  delete: (siteId: string) => apiClient.delete(`/clients/site/${siteId}`),

  block: (siteId: string) =>
    apiClient.post<{ site_id: string; cabinet_status: "active" | "blocked" }>(`/clients/site/${siteId}/block`),

  unblock: (siteId: string) =>
    apiClient.post<{ site_id: string; cabinet_status: "active" | "blocked" }>(`/clients/site/${siteId}/unblock`),

  resetPassword: (siteId: string, newPassword?: string) =>
    apiClient.post(`/clients/site/${siteId}/reset-password`, { new_password: newPassword }),

  provision: (siteId: string, initialConfig?: Record<string, unknown>) =>
    apiClient.post(`/clients/site/${siteId}/provision`, { initial_config: initialConfig }),

  requestApproval: (siteId: string, action: "delete" | "reset_password") =>
    apiClient.post(`/clients/site/${siteId}/request-approval`, { action }),
};

// ==================== Support Tickets API ====================

export const supportTicketsApi = {
  // List all tickets (for Kanban board)
  list: (params?: { status?: string }) => {
    const queryParams: Record<string, string> = {};
    if (params?.status) queryParams.status = params.status;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = queryString ? `/support/tickets?${queryString}` : "/support/tickets";
    return apiClient.get<SupportTicket[]>(url);
  },

  // Get single ticket
  get: (id: string) => apiClient.get<SupportTicket>(`/support/tickets/${id}`),

  // Update ticket status
  updateStatus: (id: string, status: "new" | "in_progress" | "resolved" | "rejected") =>
    apiClient.patch(`/support/tickets/${id}/status`, { status }),

  // Accept assigned ticket
  accept: (id: string) => apiClient.post<SupportTicket>(`/support/tickets/${id}/accept`, {}),

  // Reject current assignment without closing the ticket
  rejectAssignment: (id: string) =>
    apiClient.post<{ status: string }>(`/support/tickets/${id}/reject-assignment`, {}),

  // Manual assignment by supervisor/director/owner
  assign: (id: string, managerId: string) =>
    apiClient.post<SupportTicket>(`/support/tickets/${id}/assign`, { manager_id: managerId }),

  // Get messages for a ticket
  getMessages: (ticketId: string) =>
    apiClient.get<SupportTicketMessage[]>(`/support/tickets/${ticketId}/messages`),

  // Send a message
  sendMessage: (ticketId: string, data: { message: string }) =>
    apiClient.post<SupportTicketMessage>(`/support/tickets/${ticketId}/messages`, data),

  // Send an internal note
  sendInternalNote: (ticketId: string, data: { message: string }) =>
    apiClient.post<SupportTicketMessage>(`/support/tickets/${ticketId}/internal-note`, data),

  // Get ticket attachments
  listAttachments: (ticketId: string) =>
    apiClient.get<SupportTicketAttachment[]>(`/support/tickets/${ticketId}/attachments`),

  // Upload a ticket attachment, optionally scoped to a message
  uploadAttachment: (ticketId: string, file: File, messageId?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    if (messageId) {
      formData.append("message_id", messageId);
    }
    return apiClient.post<SupportTicketAttachment>(`/support/tickets/${ticketId}/attachments`, formData);
  },

  // Get templates
  getTemplates: () => apiClient.get<SupportTicketTemplate[]>("/support/templates"),
};

export interface DirectDeployStartResponse {
  success: boolean;
  deploy_id: string;
  status: string;
  server_name?: string;
}

export interface DirectDeployStatusResponse {
  success: boolean;
  deploy_id: string;
  status: string;
}

export interface CodePublicationPreflight {
  site_id: string;
  domain: string;
  source_binding_id: string;
  active_release_id: string | null;
  published_config_version: number;
  published_config_sha256: string;
  has_unpublished_draft: boolean;
  publication_pipeline_enabled: boolean;
  active_publication_id?: string;
}

export interface ContentPublicationPreflight extends CodePublicationPreflight {
  draft_config_version: number;
  draft_config_sha256: string;
  changed_fields: string[];
  backend_ready: boolean;
}

export interface ContentPublicationRequest {
  expected_version: number;
  expected_draft_sha256: string;
  expected_active_release_id: string;
  idempotency_key: string;
  reason: string;
}

export interface SiteCodePublicationRequest {
  legacyBootstrap?: boolean;
  cmsSiteId: string;
  archive: File;
  reason: string;
  preflight: CodePublicationPreflight;
  idempotencyKey: string;
}

export interface SiteCodePublicationStartResponse {
  success: boolean;
  operation_id: string;
  publication_id: string;
  site_id: string;
  status: string;
  source_sha256: string;
  config_source: "live";
  replayed: boolean;
}

export interface SiteCodePublication {
  id: string;
  site_id: string;
  config_version: number;
  config_sha256: string;
  status: string;
  stage: string;
  progress_percent: number;
  revision: number;
  message: string;
  release_id?: string;
  previous_release_id?: string;
  build_operation_id?: string;
  artifact_sha256?: string;
  trigger_kind: "admin_code";
  config_source: "live";
  source_candidate_id?: string;
  source_sha256?: string;
  source_size_bytes?: number;
  source_revision?: string;
  reason?: string;
  correlation_id?: string;
  activated_at?: string;
  commit_acknowledged_at?: string;
  cancel_requested: boolean;
  created_at: string;
}

export interface SiteCodePublicationEvent {
  seq: number;
  status: string;
  stage: string;
  progress_percent: number;
  revision: number;
  message: string;
  created_at: string;
}

// Owner Dashboard API
export const ownerApi = {
  // Get dashboard stats with percentage changes
  getStats: () => apiClient.get<OwnerDashboardStats>("/owner/stats"),

  // Get timeseries data
  getRevenueTimeseries: (params: { period: string; days: number }) =>
    apiClient.get<{ data: TimeseriesPoint[] }>(`/owner/timeseries/revenue?period=${params.period}&days=${params.days}`),

  getRequestsTimeseries: (params: { period: string; days: number }) =>
    apiClient.get<{ data: TimeseriesPoint[] }>(`/owner/timeseries/requests?period=${params.period}&days=${params.days}`),

  getUserActivityTimeseries: (params: { period: string; days: number }) =>
    apiClient.get<{ data: TimeseriesPoint[] }>(`/owner/timeseries/activity?period=${params.period}&days=${params.days}`),

  // Get distribution data
  getRequestsByStatus: (days: number) =>
    apiClient.get<{ data: StatusDistribution[] }>(`/owner/distribution/requests?days=${days}`),

  getSitesByDeployStatus: () =>
    apiClient.get<{ data: StatusDistribution[] }>("/owner/distribution/sites"),

  getUsersByStatus: () =>
    apiClient.get<{ data: StatusDistribution[] }>("/owner/distribution/users"),

  // Get top performers
  getTopManagers: (limit: number) =>
    apiClient.get<{ data: TopManager[] }>(`/owner/top-managers?limit=${limit}`),

  // Get recent activity
  getRecentActivity: (limit: number) =>
    apiClient.get<{ data: RecentActivity[] }>(`/owner/recent-activity?limit=${limit}`),

  startDirectDeploy: async (
    archive: File,
    onProgress: (percentage: number) => void,
    signal?: AbortSignal,
  ) => {
    const formData = new FormData();
    formData.append("archive", archive, archive.name);
    const response = await api.post<DirectDeployStartResponse>("/owner/deploy", formData, {
      signal,
      onUploadProgress: ({ loaded, total }) => {
        if (!total) return;
        onProgress(Math.min(100, Math.round((loaded / total) * 100)));
      },
    });
    return response.data;
  },

  getDirectDeployStatus: (deployId: string, signal?: AbortSignal) =>
    apiClient.get<DirectDeployStatusResponse>(
      `/owner/deploy/${encodeURIComponent(deployId)}/status`,
      signal,
    ),

  getCodePublicationPreflight: (cmsSiteId: string, signal?: AbortSignal) =>
    apiClient.get<{ success: boolean; data: CodePublicationPreflight }>(
      `/owner/sites/${encodeURIComponent(cmsSiteId)}/code-publications/preflight`,
      signal,
    ),

  getContentPublicationPreflight: (cmsSiteId: string, signal?: AbortSignal) =>
    apiClient.get<{ success: boolean; data: ContentPublicationPreflight }>(
      `/owner/sites/${encodeURIComponent(cmsSiteId)}/content-publications/preflight`, signal,
    ),

  startContentPublication: (cmsSiteId: string, request: ContentPublicationRequest) =>
    apiClient.post<{ success: boolean; data: SiteCodePublication; replayed: boolean }>(
      `/owner/sites/${encodeURIComponent(cmsSiteId)}/content-publications`, request,
    ),

  getContentPublication: (cmsSiteId: string, publicationId: string, signal?: AbortSignal) =>
    apiClient.get<{ success: boolean; data: SiteCodePublication }>(
      `/owner/sites/${encodeURIComponent(cmsSiteId)}/content-publications/${encodeURIComponent(publicationId)}`, signal,
    ),

  startCodePublication: async (
    request: SiteCodePublicationRequest,
    onProgress: (percentage: number) => void,
    signal?: AbortSignal,
  ) => {
    const formData = new FormData();
    formData.append("archive", request.archive, request.archive.name);
    formData.append("reason", request.reason);
    const mode = codePublicationMode(request.preflight, request.legacyBootstrap);
    if (!mode) throw new Error("Публикация требует актуальной проверки и подтверждения первого перехода.");
    if (mode === "legacy") formData.append("legacy_bootstrap", "true");
    else formData.append("expected_active_release_id", request.preflight.active_release_id!);
    formData.append("expected_published_config_version", String(request.preflight.published_config_version));
    formData.append("expected_config_sha256", request.preflight.published_config_sha256);
    formData.append("idempotency_key", request.idempotencyKey);

    const response = await api.post<SiteCodePublicationStartResponse>(
      `/owner/sites/${encodeURIComponent(request.cmsSiteId)}/code-publications`,
      formData,
      {
        signal,
        onUploadProgress: ({ loaded, total }) => {
          if (!total) return;
          onProgress(Math.min(100, Math.round((loaded / total) * 100)));
        },
      },
    );
    return response.data;
  },

  getCodePublication: (cmsSiteId: string, publicationId: string, signal?: AbortSignal) =>
    apiClient.get<{ success: boolean; data: SiteCodePublication }>(
      `/owner/sites/${encodeURIComponent(cmsSiteId)}/code-publications/${encodeURIComponent(publicationId)}`,
      signal,
    ),

  getCodePublicationEvents: (cmsSiteId: string, publicationId: string, afterSeq: number, signal?: AbortSignal) =>
    apiClient.get<{ success: boolean; data: { events: SiteCodePublicationEvent[]; has_more: boolean } }>(
      `/owner/sites/${encodeURIComponent(cmsSiteId)}/code-publications/${encodeURIComponent(publicationId)}/events?after_seq=${afterSeq}`,
      signal,
    ),

  cancelCodePublication: async (cmsSiteId: string, publicationId: string, signal?: AbortSignal) => {
    const response = await api.post<{ success: boolean; data: SiteCodePublication }>(
      `/owner/sites/${encodeURIComponent(cmsSiteId)}/code-publications/${encodeURIComponent(publicationId)}/cancel`,
      {},
      { signal },
    );
    return response.data;
  },
};

// ==================== Lead Routing API ====================
// Routes go through nginx to lead-routing-service (port 8096)
// /api/v1/leads/* → lead-routing-service
// /api/v1/supervisor/* → lead-routing-service
// /api/v1/activity/* → lead-routing-service

export const leadsApi = {
  // Supervisor: Dashboard summary, dynamics and manager performance
  getDashboard: (days = 7) =>
    apiClient.get<{ success: boolean; data: LeadDashboard }>(`/supervisor/dashboard?days=${days}`),

  // Manager: Get pending leads assigned to me
  getPending: () =>
    apiClient.get<{ success: boolean; data: { leads: Lead[] } }>("/leads/pending"),

  // Manager: Get my accepted leads
  getMy: (params?: { limit?: number; offset?: number }) => {
    const queryParams: Record<string, string> = {};
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);
    if (params?.offset !== undefined) queryParams.offset = String(params.offset);
    const queryString = new URLSearchParams(queryParams).toString();
    return apiClient.get<{ success: boolean; data: { leads: Lead[] } }>(
      queryString ? `/leads/my?${queryString}` : "/leads/my"
    );
  },

  // Manager: Accept a lead
  accept: (leadId: string) =>
    apiClient.post<{ success: boolean; data: Lead }>(`/leads/${leadId}/accept`),

  // Manager: Reject a lead
  reject: (leadId: string) =>
    apiClient.post<{ success: boolean; data: { message: string } }>(`/leads/${leadId}/reject`),

  // Manager: Complete a lead
  complete: (leadId: string) =>
    apiClient.patch<{ success: boolean; data: Lead }>(`/leads/${leadId}/complete`),

  // Manager: Send heartbeat (I'm active)
  heartbeat: () =>
    apiClient.post<{ success: boolean }>("/activity/heartbeat"),

  // Supervisor: Get escalated leads
  getEscalated: (params?: { limit?: number; offset?: number }) => {
    const queryParams: Record<string, string> = {};
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);
    if (params?.offset !== undefined) queryParams.offset = String(params.offset);
    const queryString = new URLSearchParams(queryParams).toString();
    return apiClient.get<{ success: boolean; data: { escalations: LeadEscalation[] } }>(
      queryString ? `/supervisor/leads/escalated?${queryString}` : "/supervisor/leads/escalated"
    );
  },

  // Supervisor: Get leads by status
  getByStatus: (status: string, params?: { limit?: number; offset?: number }) => {
    const queryParams: Record<string, string> = { status };
    if (params?.limit !== undefined) queryParams.limit = String(params.limit);
    if (params?.offset !== undefined) queryParams.offset = String(params.offset);
    const queryString = new URLSearchParams(queryParams).toString();
    return apiClient.get<{ success: boolean; data: { leads: Lead[] } }>(
      `/supervisor/leads?${queryString}`
    );
  },

  // Supervisor: Manual assign lead to manager
  manualAssign: (leadId: string, managerId: string) =>
    apiClient.post<{ success: boolean; data: Lead }>(`/supervisor/leads/${leadId}/assign`, {
      manager_id: managerId,
    }),

  // Supervisor: Soft archive lead with reason
  archive: (leadId: string, type: "archived" | "spam" | "duplicate" | "test", note?: string) =>
    apiClient.post<{ success: boolean; data: Lead }>(`/supervisor/leads/${leadId}/archive`, {
      type,
      note: note || undefined,
    }),

  // Supervisor: Restore archived lead back to active flow
  restore: (leadId: string) =>
    apiClient.post<{ success: boolean; data: Lead }>(`/supervisor/leads/${leadId}/restore`),

  // Supervisor: Resolve escalation
  resolveEscalation: (escalationId: string, managerId: string) =>
    apiClient.post<{ success: boolean }>(`/supervisor/escalations/${escalationId}/resolve`, {
      manager_id: managerId,
    }),

  // Supervisor: Get managers activity
  getManagersActivity: () =>
    apiClient.get<{ success: boolean; data: { managers: ManagerActivity[] } }>(
      "/supervisor/managers/activity"
    ),
};

// ==================== Wenlix Site Analytics API ====================

export const wenlixSiteAnalyticsApi = {
  getKPI: (period = "7d") =>
    apiClient.get<WenlixAnalyticsKPI>(`/wenlix-site-analytics/analytics/kpi?period=${period}`),

  getVisits: (range = "7d") =>
    apiClient.get<WenlixAnalyticsTimeseriesPoint[]>(
      `/wenlix-site-analytics/analytics/visits?range=${range}`
    ),

  getCities: (params?: { period?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.period) query.set("period", params.period);
    if (params?.limit) query.set("limit", String(params.limit));
    return apiClient.get<WenlixAnalyticsCity[]>(
      `/wenlix-site-analytics/analytics/geo/cities${query.toString() ? `?${query.toString()}` : ""}`
    );
  },

  getFunnel: (steps: string, period = "7d") =>
    apiClient.get<WenlixAnalyticsFunnel>(
      `/wenlix-site-analytics/analytics/funnel?period=${period}&steps=${encodeURIComponent(steps)}`
    ),

  getPages: (params?: { period?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.period) query.set("period", params.period);
    if (params?.limit) query.set("limit", String(params.limit));
    return apiClient.get<{ items: WenlixAnalyticsPageStat[] }>(
      `/wenlix-site-analytics/analytics/pages${query.toString() ? `?${query.toString()}` : ""}`
    );
  },

  getSections: (params?: { period?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.period) query.set("period", params.period);
    if (params?.limit) query.set("limit", String(params.limit));
    return apiClient.get<{ items: WenlixAnalyticsSectionStat[] }>(
      `/wenlix-site-analytics/analytics/sections${query.toString() ? `?${query.toString()}` : ""}`
    );
  },

  getClicks: (params?: { period?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.period) query.set("period", params.period);
    if (params?.limit) query.set("limit", String(params.limit));
    return apiClient.get<{ items: WenlixAnalyticsClickStat[] }>(
      `/wenlix-site-analytics/analytics/clicks${query.toString() ? `?${query.toString()}` : ""}`
    );
  },

  getSources: (params?: { period?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.period) query.set("period", params.period);
    if (params?.limit) query.set("limit", String(params.limit));
    return apiClient.get<{ items: WenlixAnalyticsSourceStat[] }>(
      `/wenlix-site-analytics/analytics/sources${query.toString() ? `?${query.toString()}` : ""}`
    );
  },

  getDevices: (period = "7d") =>
    apiClient.get<WenlixAnalyticsDeviceBreakdown>(`/wenlix-site-analytics/analytics/devices?period=${period}`),

  getRecentEvents: (params?: { period?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.period) query.set("period", params.period);
    if (params?.limit) query.set("limit", String(params.limit));
    return apiClient.get<{ items: WenlixAnalyticsRecentEvent[] }>(
      `/wenlix-site-analytics/analytics/events/recent${query.toString() ? `?${query.toString()}` : ""}`
    );
  },

  getSessions: (params?: { period?: string; page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.period) query.set("period", params.period);
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    return apiClient.get<{ items: WenlixAnalyticsSessionSummary[]; total: number; page: number; limit: number }>(
      `/wenlix-site-analytics/analytics/sessions${query.toString() ? `?${query.toString()}` : ""}`
    );
  },

  getSessionDetail: (sessionID: string) =>
    apiClient.get<WenlixAnalyticsSessionDetail>(
      `/wenlix-site-analytics/analytics/sessions/${encodeURIComponent(sessionID)}`
    ),

  getLeads: (params?: { period?: string; page?: number; limit?: number; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.period) query.set("period", params.period);
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.status) query.set("status", params.status);
    return apiClient.get<{ items: WenlixAnalyticsLead[]; total: number; page: number; limit: number }>(
      `/wenlix-site-analytics/analytics/leads${query.toString() ? `?${query.toString()}` : ""}`
    );
  },

  updateLeadStatus: (leadID: string, status: string) =>
    apiClient.patch<WenlixAnalyticsLead>(`/wenlix-site-analytics/analytics/leads/${leadID}/status`, {
      status,
    }),

  exportLeads: async (params?: { from?: string; to?: string }) => {
    const query = new URLSearchParams();
    if (params?.from) query.set("from", params.from);
    if (params?.to) query.set("to", params.to);
    const response = await api.get<Blob>(
      `/wenlix-site-analytics/analytics/leads/export${query.toString() ? `?${query.toString()}` : ""}`,
      { responseType: "blob" }
    );
    return response.data;
  },
};

// ==================== Gamification API ====================

export const gamificationApi = {
  // Get user gamification profile
  getProfile: () => apiClient.get<UserGamificationProfile>("/gamification/profile"),

  // Get enhanced profile with all stats
  getEnhancedProfile: () => apiClient.get<EnhancedProfileResponse>("/gamification/profile/enhanced"),

  // Get all achievements with progress
  getAchievements: () => apiClient.get<{ items: AchievementWithProgress[] }>("/gamification/achievements"),

  // Get only unlocked achievements
  getUnlockedAchievements: () => apiClient.get<{ items: AchievementWithProgress[] }>("/gamification/achievements/unlocked"),

  // Get all level configurations
  getLevels: () => apiClient.get<{ items: LevelConfig[] }>("/gamification/levels"),

  // Get leaderboard
  getLeaderboard: (params?: { sort?: "level" | "xp"; limit?: number }) => {
    const queryParams: Record<string, string> = {};
    if (params?.sort) queryParams.sort = params.sort;
    if (params?.limit) queryParams.limit = String(params.limit);
    const queryString = new URLSearchParams(queryParams).toString();
    return apiClient.get<{ items: LeaderboardEntry[] }>(
      queryString ? `/gamification/leaderboard?${queryString}` : "/gamification/leaderboard"
    );
  },

  // Get user rank
  getRank: () => apiClient.get<{ rank: number }>("/gamification/rank"),

  // Admin: Get gamification stats
  getStats: () => apiClient.get<GamificationStats>("/gamification/admin/stats"),

  // Admin: Add manual XP to user
  addManualXP: (userId: string, data: { amount: number; reason: string }) =>
    apiClient.post(`/gamification/admin/users/${userId}/xp`, data),
};
